import usePrivateAxios from '@/common/api/usePrivateAxios'
import { notificationTransformerResponse } from '@/common/constants/notificationTransformerResponse.constant'
import { Mapper } from '@/common/services/mapper'
import { useNotificationStore } from '@/common/stores/useNotificationStore'
import { MetaResponse } from '@/common/types/metaResponse'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'

type NotificationMetaResponse = MetaResponse & {
  not_seen_count: number
}

type UseNotificationsRecipientMessagesProps<T> = {
  params?: {
    sendDisplayIdentifier?: boolean
    page?: number
    pageSize?: number
    refetchInterval?: number | false
    qOnlyNotSeen?: boolean
  }
  mapper?: (data: any) => T
  queryKey?: string
}

export const useNotificationsRecipientMessages = <T,>({
  params: { sendDisplayIdentifier = false, page, pageSize, refetchInterval = false, qOnlyNotSeen = false } = {},
  mapper = Mapper.default<T>(),
  queryKey = 'notificationsRecipientMessages'
}: UseNotificationsRecipientMessagesProps<T>) => {
  const privateAxios = usePrivateAxios()

  const { setLastDisplayIdentifier } = useNotificationStore()

  const {
    data: { data, meta } = notificationTransformerResponse<T>(),
    isPending,
    ...rest
  } = useQuery({
    queryKey: [queryKey, page, pageSize, qOnlyNotSeen],
    queryFn: () => {
      const params = {
        display_identifier: undefined as string | undefined,
        page,
        page_size: pageSize,
      }

      if (sendDisplayIdentifier) {
        const displayIdentifier = uuid()

        setLastDisplayIdentifier(displayIdentifier)

        params.display_identifier = displayIdentifier
      }

      return privateAxios.get('/notifications/recipient_messages', {
        params: {
          page,
          page_size: pageSize,
          display_identifier: params.display_identifier,
          ...(qOnlyNotSeen ? { 'q[only_not_seen]': qOnlyNotSeen } : {}),
        },
      })
    },
    select: (data: { data: { data: T[]; meta: NotificationMetaResponse } }) => {
      return {
        data: mapper(data?.data?.data),
        meta: data?.data?.meta,
      }
    },
    placeholderData: keepPreviousData,
    refetchInterval,
  })

  return { data, meta, isPending, ...rest }
}
